import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  setPolicyParams,
  setInitialPropertiesForSDK,
} from '../../addToWallet/AddToWalletIntegrationSDK';
import { CLIENT_ID_PAYSOL } from '../../../utils/constants/genericConstants';
import { getAccountDetailsContent } from '../../../store/contentSlice';
import { getSelectedClientFeature } from '../../../store/featureSlice';
import PendedAccountCard from '../../pendedAccountCard/PendedAccountCard';
import SingleUseCardLink from '../linkButtons/SingleUseCardLink';
import ShoppingPassLink from '../linkButtons/ShoppingPassLink';
import CreditIncreaseLink from '../linkButtons/CreditIncreaseLink';
import AddToWallet from '../../addToWallet/AddToWallet';
import TermsAndConditions from '../../termsAndConditions/TermsAndConditions';
import { showDateInFormat, formatAmount } from '../../../utils/helpers';

const AccountDetails: FunctionComponent = (props: any) => {
  const {
    availableToSpendText,
    currentLimitText,
    asOfDate,
    usDollarSymbol,
    singleUseCardButtonText,
    shoppingPassButtonText,
    creditIncreaseButtonText,
    termsAndConditionsText,
  }: any = useSelector(getAccountDetailsContent);
  const {
    accountId,
    displayName,
    availableCredit,
    creditLimit,
    isPended,
    asOfDate: asOfDateValue,
    accountOpenDate,
    features,
    index,
    cardType,
    showAccountDetails,
    isAccordionCollapsed,
    productType,
    clientId,
    portfolio,
  } = props;
  const {
    accessToAppleWallet,
    accessToGoogleWallet,
    accessToSingleUseCard,
    accessToTokenizedShoppingPass,
  } = features;

  const selectedClientId = clientId === CLIENT_ID_PAYSOL ? portfolio : clientId;

  const clientSelectedFeatures = useSelector((state: any) =>
    getSelectedClientFeature(state, selectedClientId),
  );
  const { accessToCreditLimitIncrease } = clientSelectedFeatures.features;

  // should have access to at-least one of the wallets
  const accessToAddToWallet = accessToAppleWallet || accessToGoogleWallet;

  useEffect(() => {
    if (showAccountDetails && !isPended) {
      //setInitialize add to wallet policyParams
      if (accessToAppleWallet) {
        const appleWalletButtonId = `applePayWalletButton_${index}`;
        /*Setting up initialize params for add to wallet SDK*/
        setInitialPropertiesForSDK(accountId, cardType);
        setPolicyParams(props, appleWalletButtonId);
      } else if (!accessToAppleWallet && accessToGoogleWallet) {
        /*Setting up initialize params for add to wallet SDK*/
        setInitialPropertiesForSDK(accountId, cardType);
        setPolicyParams(props);
      }
    }
  }, [showAccountDetails]);

  return (
    <>
      {isPended ? (
        <PendedAccountCard clientId={clientId} />
      ) : (
        <>
          <div className="card-amount-wrapper">
            <div className="amount-info-first">
              <div className="available-credit-wrapper">
                <div
                  className="available-credit-amount"
                  data-testid="account-details-available-credit"
                >
                  {usDollarSymbol}
                  {formatAmount(availableCredit)}
                </div>
                <div
                  className="available-to-spend-text"
                  data-testid="account-details-available-spend-text"
                >
                  {availableToSpendText}
                </div>
              </div>
              <div className="card-details-divider-wrapper">
                <span className="card-details-divider"></span>
              </div>
              <div className="current-limit-wrapper">
                <div
                  className="available-current-limit"
                  data-testid="account-details-current-limit"
                >
                  {usDollarSymbol}
                  {formatAmount(creditLimit)}
                </div>
                <div
                  className="current-limit-text"
                  data-testid="account-details-current-limit-text"
                >
                  {currentLimitText}
                </div>
              </div>
            </div>
            <div
              className="date-info-span"
              data-testid="account-details-date-info"
            >
              {asOfDate} {showDateInFormat(asOfDateValue)}
            </div>
          </div>
          {accessToAddToWallet ? (
            <AddToWallet {...props} />
          ) : (
            <div className="add-to-wallet-empty-wrapper"></div>
          )}
          <TermsAndConditions
            productType={productType}
            accountOpenDate={accountOpenDate}
            asOfDateValue={asOfDateValue}
            selectedClient={selectedClientId}
            termsAndConditionsText={termsAndConditionsText}
          />
          <div className="features-link-content">
            {accessToSingleUseCard && (
              <SingleUseCardLink
                accountId={accountId}
                displayName={displayName}
                singleUseCardButtonText={singleUseCardButtonText}
                isAccordionCollapsed={isAccordionCollapsed}
              />
            )}
            {accessToTokenizedShoppingPass && (
              <ShoppingPassLink
                accountId={accountId}
                displayName={displayName}
                shoppingPassButtonText={shoppingPassButtonText}
              />
            )}
            {accessToCreditLimitIncrease && (
              <CreditIncreaseLink
                accountId={accountId}
                displayName={displayName}
                creditIncreaseButtonText={creditIncreaseButtonText}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AccountDetails;
